import { SpaMode } from './spamode';
import { LocationHelper } from './utility/location-helper';

type PushState = History['pushState'];
type ReplaceState = History['replaceState'];

type GlobalHistoryLikeObject = {
  history: {
    pushState: PushState;
    replaceState: ReplaceState;
    state?: any;
  };
  addEventListener: EventTarget['addEventListener'];
};

type LocationLike = Pick<Location, 'pathname' | 'search' | 'href' | 'hash'>;

class HistoryAPIPatcher {
  private lastLocation!: Pick<Location, 'pathname' | 'hash' | 'search'>;
  private pushStateCache: PushState;
  private replaceStateCache: ReplaceState;

  constructor(
    private globalObject: GlobalHistoryLikeObject,
    private reproio: (key: string, ...args: any[]) => void,
    private location: LocationLike
  ) {
    this.pushStateCache = this.globalObject.history.pushState;
    this.replaceStateCache = this.globalObject.history.replaceState;
    this.recordLastLocation();
  }

  public patch() {
    const pushState: PushState = (...args) => {
      const { pathname, search, hash } = this.location;
      // pushStateを最初に行っておくことでlocationの値を合わせる
      this.pushStateCache.apply(this.globalObject.history, args);
      if (
        !LocationHelper.isSamePath(pathname, this.location.pathname) ||
        !LocationHelper.isSameQuery(search, this.location.search)
      ) {
        this.dispatchPageChangeToReproio(false);
      } else if (!LocationHelper.isSameHash(hash, this.location.hash)) {
        this.dispatchPageChangeToReproio(true);
      }
      this.recordLastLocation();
    };

    const replaceState: ReplaceState = (...args) => {
      const { pathname, search, hash } = this.location;
      // replaceState自体は何があっても実行する
      this.replaceStateCache.apply(this.globalObject.history, args);
      if (
        !LocationHelper.isSamePath(this.location.pathname, pathname) ||
        !LocationHelper.isSameQuery(this.location.search, search)
      ) {
        this.dispatchPageChangeToReproio(false);
      } else if (!LocationHelper.isSameHash(hash, this.location.hash)) {
        this.dispatchPageChangeToReproio(true);
      }
    };

    this.globalObject.history.pushState = pushState;
    this.globalObject.history.replaceState = replaceState;

    const handlePopstate = () => {
      const { pathname, search, hash } = this.lastLocation;
      if (
        !LocationHelper.isSamePath(pathname, this.location.pathname) ||
        !LocationHelper.isSameQuery(search, this.location.search)
      ) {
        this.dispatchPageChangeToReproio(false);
      } else if (!LocationHelper.isSameHash(hash, this.location.hash)) {
        this.dispatchPageChangeToReproio(true);
      }
      this.recordLastLocation();
    };
    this.globalObject.addEventListener('popstate', handlePopstate);
  }

  private dispatchPageChangeToReproio(treatAsHash: boolean) {
    if (treatAsHash) {
      this.reproio('_handlePageChange', SpaMode.HASH);
    } else {
      this.reproio('_handlePageChange', SpaMode.HISTORY);
    }
  }

  private recordLastLocation() {
    const { pathname, search, hash } = this.location;
    this.lastLocation = {
      pathname,
      search,
      hash,
    };
  }
}

export const patchHistoryAPI = (
  globalObject: GlobalHistoryLikeObject,
  reproio: (key: string, ...args: any[]) => void,
  location: Pick<Location, 'pathname' | 'search' | 'href' | 'hash'>
) => {
  new HistoryAPIPatcher(globalObject, reproio, location).patch();
};
